import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { TabBar, TabBarProps, TabView } from 'react-native-tab-view';

import { LEFT_ALIGNED_TAB_BAR_CONFIG, TopTabBarLabel } from '@/components/TopTabBar';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

type TabRoute = {
    key: string;
    title: string;
};

export type LeftSideTabBarProps = {
    tabs: Array<{ title: string; id: string }>;
    setSelectedTab?: (id: string) => void;
    renderTabBarItem?: TabBarProps<TabRoute>['renderTabBarItem'];
    renderIndicator?: TabBarProps<TabRoute>['renderIndicator'];
    containerStyle?: ViewStyle;
    tabBarStyle?: ViewStyle;
    indicatorStyle?: ViewStyle;
    gap?: number;
    initialIndex?: number;
    location: string;
};

export const TABS_HEIGHT = 44;

const config = {
    ...LEFT_ALIGNED_TAB_BAR_CONFIG,
    tabBarIndicatorStyle: {
        ...(LEFT_ALIGNED_TAB_BAR_CONFIG.tabBarIndicatorStyle as object),
        backgroundColor: designSystem.colors.liliac,
    },
};

/**
 * A Tab Bar with titles aligned on the left side with no screens
 */
export const LeftSideTabBar = memo(
    ({
        tabs,
        setSelectedTab,
        renderTabBarItem,
        indicatorStyle,
        renderIndicator,
        containerStyle,
        tabBarStyle,
        gap = 24,
        initialIndex = 0,
        location,
    }: LeftSideTabBarProps) => {
        const [index, setIndex] = React.useState(initialIndex);

        useEffect(() => {
            setIndex(initialIndex);
        }, [initialIndex]);

        const routes = useMemo(
            () =>
                tabs.map(tab => ({
                    key: tab.id,
                    title: tab.title,
                })),
            [tabs]
        );

        const renderLabel = useCallback(
            (it: { focused: boolean; route: { key: string; title: string } }) => {
                if (renderTabBarItem) {
                    return undefined;
                }
                return <TopTabBarLabel focused={it.focused}>{it.route.title || ''}</TopTabBarLabel>;
            },
            [renderTabBarItem]
        );

        const renderTabBar = useCallback(
            (props: TabBarProps<TabRoute>) => {
                return (
                    <TabBar
                        onTabPress={({ route }) => {
                            setIndex(routes.findIndex(item => item.key === route.key));
                            setSelectedTab?.(route.key);
                            BetrAnalytics.trackEvent(AnalyticsEvent.TAB, {
                                tab_title: route.title,
                                location: location,
                            });
                        }}
                        {...props}
                        scrollEnabled={true}
                        style={[config.tabBarStyle, tabBarStyle]}
                        gap={gap}
                        indicatorStyle={[config.tabBarIndicatorStyle, indicatorStyle]}
                        renderIndicator={renderIndicator}
                        tabStyle={config.tabBarItemStyle}
                        renderLabel={renderLabel}
                        renderTabBarItem={renderTabBarItem}
                    />
                );
            },
            [
                gap,
                indicatorStyle,
                location,
                renderIndicator,
                renderLabel,
                renderTabBarItem,
                routes,
                setSelectedTab,
                tabBarStyle,
            ]
        );

        return (
            <MaxWidthWrapper style={[styles.container, containerStyle]}>
                <View style={styles.tabsContainer}>
                    <TabView
                        onIndexChange={NO_OP}
                        navigationState={{ index, routes }}
                        renderScene={NO_VIEW}
                        renderTabBar={renderTabBar}
                    />
                </View>
            </MaxWidthWrapper>
        );
    }
);

const NO_OP = () => {};
const NO_VIEW = () => <></>;

const styles = StyleSheet.create({
    container: {
        backgroundColor: isWeb ? undefined : designSystem.colors.gray8,
        flex: isWeb ? undefined : 1,
        height: TABS_HEIGHT,
    },
    tabsContainer: {
        flexDirection: 'row',
        position: 'relative',
    },
});
